import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import axios from 'axios';
import Dashboard from './user/Dashboard';
import ProfileSettings from './user/ProfileSettings';
import ProfileSearch from './user/ProfileSearch';
import ProfileEditModal from './user/ProfileEditModal';
import FavoriteList from './user/FavoriteList';
import UnreadEmails from './user/UnreadEmails';
import SentEmails from './user/SentEmails';
import ReceivedEmails from './user/ReceivedEmails';
import MessageBoardPosting from './user/MessageBoardPosting';
import MessageBoardSearch from './user/MessageBoardSearch';
import Inquiry from './user/Inquiry';
import InquiryHistory from './user/InquiryHistory';
import PointsUsage from './user/PointsUsage';
import PointPurchase from './user/PointPurchase';
import {IoCheckmarkSharp, IoCheckmarkDoneSharp } from "react-icons/io5"; 
import { HiMenu, HiX } from "react-icons/hi";

const API_URL = process.env.REACT_APP_API_URL;

const UserPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [annualIncomes, setAnnualIncomes] = useState([]);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileData, setProfileData] = useState({
    currentPassword: '',
    newPassword: '',
    name: '',
    birthday: '',
    address: '',
    photoLink: '',
    occupation: '',
    annual_income: ''
  });

  // Layout constants for mobile to avoid covering header/footer
  const HEADER_HEIGHT = isMobile ? 60 : 0; // approximate header height on mobile
  const FOOTER_HEIGHT = 90 ; // footer height if fixed

  // Sidebar visibility helper for styling transitions
  const isSidebarVisible = !(isMobile && sidebarCollapsed);

  useEffect(() => {
    fetchUserData();
    fetchOccupations();
    fetchAnnualIncomes();
    
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      
      // Auto-show sidebar on mobile when screen size changes to mobile
      if (newIsMobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };
    
    // Set initial mobile state and auto-hide sidebar on mobile
    const initialIsMobile = window.innerWidth <= 768;
    setIsMobile(initialIsMobile);
    if (initialIsMobile) {
      setSidebarCollapsed(true);
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync activeSection with current URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/user' || path === '/user/dashboard') {
      setActiveSection('dashboard');
    } else if (path === '/user/profile-settings') {
      setActiveSection('profile');
    } else if (path === '/user/profile-search') {
      setActiveSection('profileSearch');
    } else if (path === '/user/favorite-list') {
      setActiveSection('favoriteList');
    } else if (path === '/user/unread-emails') {
      setActiveSection('unreadEmails');
    } else if (path === '/user/sent-emails') {
      setActiveSection('sentEmails');
    } else if (path === '/user/received-emails') {
      setActiveSection('receivedEmails');
    } else if (path === '/user/message-board-posting') {
      setActiveSection('messageBoardPosting');
    } else if (path === '/user/message-board-search') {
      setActiveSection('messageBoardSearch');
    } else if (path === '/user/inquiry') {
      setActiveSection('inquiry');
    } else if (path === '/user/inquiry-history') {
      setActiveSection('inquiryHistory');
    } else if (path === '/user/points-usage') {
      setActiveSection('pointsUsage');
    } else if (path === '/user/point-purchase') {
      setActiveSection('pointPurchase');
    }
  }, [location.pathname]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setProfileData({
        currentPassword: '',
        newPassword: '',
        name: response.data.name || '',
        birthday: response.data.birthday || '',
        address: response.data.address || '',
        photoLink: response.data.photoLink || '',
        occupation: response.data.occupation?._id || '',
        annual_income: response.data.annual_income?._id || ''
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchOccupations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/occupations`);
      setOccupations(response.data);
    } catch (error) {
      console.error('Error fetching occupations:', error);
    }
  };

  const fetchAnnualIncomes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/annual-incomes`);
      setAnnualIncomes(response.data);
    } catch (error) {
      console.error('Error fetching annual incomes:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const handleEditProfile = async () => {
    try {
      // Fetch latest user data before opening the modal
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update both user and profileData with latest information
      setUser(response.data);
      setProfileData({
        currentPassword: '',
        newPassword: '',
        name: response.data.name || '',
        birthday: response.data.birthday || '',
        address: response.data.address || '',
        photoLink: response.data.photoLink || '',
        occupation: response.data.occupation?._id || '',
        annual_income: response.data.annual_income?._id || ''
      });
      
      // Now open the modal with fresh data
      setShowProfileEditModal(true);
    } catch (error) {
      console.error('Error fetching latest user data:', error);
      // Fallback: open modal with existing data
      setShowProfileEditModal(true);
    }
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigationClick = (section) => {
    // Auto-hide sidebar on mobile when navigation item is clicked
    if (isMobile) {
      setSidebarCollapsed(true);
    }
    
    // Navigate to the appropriate URL
    switch (section) {
      case 'dashboard':
        navigate('/user/dashboard');
        break;
      case 'profile':
        navigate('/user/profile-settings');
        break;
      case 'profileSearch':
        navigate('/user/profile-search');
        break;
      case 'favoriteList':
        navigate('/user/favorite-list');
        break;
      case 'unreadEmails':
        navigate('/user/unread-emails');
        break;
      case 'sentEmails':
        navigate('/user/sent-emails');
        break;
      case 'receivedEmails':
        navigate('/user/received-emails');
        break;
      case 'messageBoardPosting':
        navigate('/user/message-board-posting');
        break;
      case 'messageBoardSearch':
        navigate('/user/message-board-search');
        break;
      case 'inquiry':
        navigate('/user/inquiry');
        break;
      case 'inquiryHistory':
        navigate('/user/inquiry-history');
        break;
      case 'pointsUsage':
        navigate('/user/points-usage');
        break;
      case 'pointPurchase':
        navigate('/user/point-purchase');
        break;
      default:
        navigate('/user/dashboard');
    }
  };

  // Function to update user points immediately after deduction
  const updateUserPoints = (newPoints) => {
    setUser(prevUser => ({
      ...prevUser,
      points: newPoints
    }));
  };

  // Update points and notify when a scheduled send deducts points
  useEffect(() => {
    const onPointsDeducted = (e) => {
      const remaining = e?.detail?.remainingPoints;
      if (typeof remaining === 'number') {
        updateUserPoints(remaining);
        // alert(`ポイントが更新されました。\n現在Pt: ${remaining}pt`);
      }
    };
    window.addEventListener('points:deducted', onPointsDeducted);
    return () => window.removeEventListener('points:deducted', onPointsDeducted);
  }, []);

  const showConfirmationDialog = (message, cost, actionName) => {
    return new Promise((resolve) => {
      const confirmed = window.confirm(`${message}\n\nコスト: ${cost} ポイント\n\n続行しますか？`);
      resolve(confirmed);
    });
  };

  const handleBackToDashboard = () => {
    navigate('/user/dashboard');
  };

  const handleLegalPage = (page) => {
    switch (page) {
      case 'age-verification':
        navigate('/legal/age-verification');
        break;
      case 'terms':
        navigate('/legal/terms-of-service');
        break;
      case 'transactions':
        navigate('/legal/transaction-law');
        break;
      case 'privacy':
        navigate('/legal/privacy-policy');
        break;
      default:
        navigate('/legal/age-verification');
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <Dashboard user={user} onEditProfile={handleEditProfile} onUserUpdate={setUser} />;
      case 'profile':
        return <ProfileSettings onEditProfile={handleEditProfile} />;
      case 'profileSearch':
        return <ProfileSearch 
          occupations={occupations} 
          annualIncomes={annualIncomes} 
          updateUserPoints={updateUserPoints}
          showConfirmationDialog={showConfirmationDialog}
          user={user}
        />;
      case 'favoriteList':
        return <FavoriteList 
          updateUserPoints={updateUserPoints}
          showConfirmationDialog={showConfirmationDialog}
          user={user}
        />;
      case 'unreadEmails':
        return <UnreadEmails />;
      case 'sentEmails':
        return <SentEmails />;
      case 'receivedEmails':
        return <ReceivedEmails />;
      case 'messageBoardPosting':
        return <MessageBoardPosting />;
      case 'messageBoardSearch':
        return <MessageBoardSearch 
          updateUserPoints={updateUserPoints}
          showConfirmationDialog={showConfirmationDialog}
          user={user}
        />;
      case 'inquiry':
        return <Inquiry />;
      case 'inquiryHistory':
        return <InquiryHistory />;
      case 'pointsUsage':
        return <PointsUsage />;
      case 'pointPurchase':
        return <PointPurchase user={user} updateUserPoints={updateUserPoints} />;
      default:
        return <div>ページが見つかりません</div>;
    }
  };

  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '2px solid #ffd700',
        paddingBottom: '10px',
        flexWrap: 'wrap',
        gap: '10px',
        position: 'sticky',
        top: 0,
        backgroundColor: '#f5f5f5',
        zIndex: 1000,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {isMobile && (
            <button
              onClick={toggleSidebar}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#333',
                padding: '5px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {sidebarCollapsed ? <HiMenu /> : <HiX />}
            </button>
          )}
                  <div style={{ 
          backgroundColor: '#ffd700', 
          padding: isMobile ? '8px 15px' : '10px 20px', 
          borderRadius: '5px'
        }}>
          <h1 style={{ 
            color: 'white', 
            margin: 0, 
            fontSize: isMobile ? '20px' : '24px' 
          }}>STYLE</h1>
        </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            backgroundColor: '#ff6b35',
            color: 'white',
            border: 'none',
            padding: isMobile ? '8px 16px' : '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: isMobile ? '12px' : '14px'
          }}
        >
          ログアウト
        </button>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '20px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Mobile Backdrop Overlay */}
        {isMobile && (
          <div
            onClick={() => setSidebarCollapsed(true)}
            style={{
              position: 'fixed',
              top: HEADER_HEIGHT,
              left: 0,
              right: 0,
              bottom: FOOTER_HEIGHT,
              backgroundColor: 'rgba(0,0,0,0.35)',
              transition: 'opacity 300ms ease',
              opacity: isSidebarVisible ? 1 : 0,
              pointerEvents: isSidebarVisible ? 'auto' : 'none',
              zIndex: 900
            }}
          />
        )}
        {/* Left Sidebar */}
        <div style={{ 
          // width: isMobile ? (sidebarCollapsed ? '0px' : '100%') : '180px',
          backgroundColor: '#f8f8f8', 
          padding: '10px',
          borderRadius: '5px',
          height: isMobile ? `calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)` : 'fit-content',
          minWidth: isMobile ? '60%' : '180px',
          overflowY: isMobile ? 'auto' : 'visible',
          transition: 'opacity 300ms ease',
          opacity: isSidebarVisible ? 1 : 0,
          pointerEvents: isSidebarVisible ? 'auto' : 'none',
          position: isMobile ? 'fixed' : 'sticky',
          top: isMobile ? HEADER_HEIGHT : 0,
          left: isMobile ? 0 : 'auto',
          bottom: isMobile ? FOOTER_HEIGHT : 'auto',
          zIndex: isMobile ? 1100 : '0',
          alignSelf: 'flex-start'
        }}>
          <div style={{ 
            backgroundColor: '#333', 
            color: 'white', 
            padding: isMobile ? '12px' : '15px',
            borderRadius: '5px',
            marginBottom: '30px'
          }}>
            <h3 style={{ 
              margin: '0 0 10px 0',
              fontSize: isMobile ? '14px' : '16px'
            }}>会員情報</h3>
            {user && (
              <>
                <p style={{ 
                  margin: '5px 0',
                  fontSize: isMobile ? '11px' : '14px'
                }}>{user.name || '未設定'}</p>
                <p style={{ 
                  margin: '5px 0',
                  fontSize: isMobile ? '11px' : '14px'
                }}>ID: {user.userID}</p>
                <p style={{ 
                  margin: '5px 0',
                  fontSize: isMobile ? '11px' : '14px'
                }}>現在Pt: {user.points}</p>
              </>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ 
              backgroundColor: '#ff6b35', 
              color: 'white', 
              padding: '10px',
              margin: '0 0 10px 0',
              borderRadius: '5px'
            }}>
              メニュー
            </h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <div style={{backgroundColor:'#e6e5e5ff'}}>
                <div style={{width:'100%', marginBottom:'-10px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
                <p  style={{textAlign:'center',fontSize:'13px', color:'#a1a1a1ff'}}>設 定</p>
                <div style={{width:'100%', marginTop:'-9px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
              </div>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: isMobile ? '6px' : '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'dashboard' ? '#ffd700' : 'transparent',
                  // fontSize: isMobile ? '12px' : '14px'
                }}
                onClick={() => handleNavigationClick('dashboard')}
              >
                • ダッシュボード
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'profile' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('profile')}
              >
                • プロフィール設定
              </li>
              <div style={{backgroundColor:'#e6e5e5ff'}}>
                <div style={{width:'100%', marginBottom:'-10px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
                <p  style={{textAlign:'center',fontSize:'13px', color:'#a1a1a1ff'}}>検  索</p>
                <div style={{width:'100%', marginTop:'-9px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
              </div>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'profileSearch' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('profileSearch')}
              >
                • プロフィール検索
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'favoriteList' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('favoriteList')}
              >
                • お気に入りリスト
              </li>
              <div style={{backgroundColor:'#e6e5e5ff'}}>
                <div style={{width:'100%', marginBottom:'-10px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
                <p  style={{textAlign:'center',fontSize:'13px', color:'#a1a1a1ff'}}>メール</p>
                <div style={{width:'100%', marginTop:'-9px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
              </div>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'unreadEmails' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('unreadEmails')}
              >
                • 未読メッセージ
              </li>

              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'receivedEmails' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('receivedEmails')}
              >
                • 受信済みメッセージ
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'sentEmails' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('sentEmails')}
              >
                • 送信済みメッセージ
              </li>
              
              <div style={{backgroundColor:'#e6e5e5ff'}}>
                <div style={{width:'100%', marginBottom:'-10px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
                <p  style={{textAlign:'center',fontSize:'13px', color:'#a1a1a1ff'}}>投稿</p>
                <div style={{width:'100%', marginTop:'-9px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
              </div>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'messageBoardPosting' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('messageBoardPosting')}
              >
                • 掲示板投稿
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'messageBoardSearch' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('messageBoardSearch')}
              >
                • 掲示板検索
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'inquiry' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('inquiry')}
              >
                • お問い合わせ
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'inquiryHistory' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('inquiryHistory')}
              >
                • お問い合わせ履歴
              </li>
              {/* <div style={{backgroundColor:'#e6e5e5ff'}}>
                <div style={{width:'100%', marginBottom:'-10px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
                <p  style={{textAlign:'center',fontSize:'13px', color:'#a1a1a1ff'}}>ポイント</p>
                <div style={{width:'100%', marginTop:'-9px', height:'1px', backgroundColor:'#a1a1a1ff'}}></div>
              </div>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'pointsUsage' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('pointsUsage')}
              >
                • ポイント使用履歴
              </li>
              <li 
                style={{ 
                  margin: '5px 0', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '5px',
                  backgroundColor: activeSection === 'pointPurchase' ? '#ffd700' : 'transparent'
                }}
                onClick={() => handleNavigationClick('pointPurchase')}
              >
                • ポイント購入
              </li> */}
            </ul>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ 
          maxWidth: isMobile && sidebarCollapsed ? "100%" : "auto",
          flex: 1, 
          backgroundColor: '#f8f8f8', 
          padding: isMobile ? '15px' : '10px',
          paddingBottom: isMobile ? '60px' : '70x',
          borderRadius: '10px',
          marginLeft: isMobile ? '0px' : '20px',
          marginTop: isMobile ? '20px' : '0px',
          // width: isMobile && sidebarCollapsed ? '100%' : 'auto'
        }}>
          {renderContent()}
        </div>
      </div>

      {/* Profile Edit Modal */}
      <ProfileEditModal
        showModal={showProfileEditModal}
        onClose={() => setShowProfileEditModal(false)}
        user={user}
        profileData={profileData}
        setProfileData={setProfileData}
        occupations={occupations}
        annualIncomes={annualIncomes}
        onProfileUpdate={handleProfileUpdate}
      />

      {/* Footer */}
      <footer style={{
        height: isMobile ? "60px" : "35px", 
        maxWidth: '100%', 
        fontSize: isMobile ? '11px' : '13px', 
        backgroundColor: 'rgba(44, 41, 41, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: isMobile ? '10px' : '0px'
      }}>
        <div style={{
          marginBottom: '0px'
        }}>
          {/* <a 
            onClick={() => handleLegalPage('age-verification')}
            style={{
              color: '#bdb7b7',
              cursor: 'pointer',
              textDecoration: 'none',
              margin: '0 10px',
              transition: 'color 0.3s ease',
              fontSize: '10px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffd700'}
            onMouseLeave={(e) => e.target.style.color = '#bdb7b7'}
          >
            年齢認証に関して
          </a>
          <span style={{ color: '#777676' }}>|</span> */}
          <a 
            onClick={() => handleLegalPage('terms')}
            style={{
              color: '#bdb7b7',
              cursor: 'pointer',
              textDecoration: 'none',
              margin: '0 10px',
              transition: 'color 0.3s ease',
              fontSize: '10px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffd700'}
            onMouseLeave={(e) => e.target.style.color = '#bdb7b7'}
          >
            利用規約
          </a>
          <span style={{ color: '#777676' }}>|</span>
          <a 
            onClick={() => handleLegalPage('transactions')}
            style={{
              color: '#bdb7b7',
              cursor: 'pointer',
              textDecoration: 'none',
              margin: '0 10px',
              transition: 'color 0.3s ease',
              fontSize: '10px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffd700'}
            onMouseLeave={(e) => e.target.style.color = '#bdb7b7'}
          >
            特定商取引法に基く表記
          </a>
          <span style={{ color: '#777676' }}>|</span>
          <a 
            onClick={() => handleLegalPage('privacy')}
            style={{
              color: '#bdb7b7',
              cursor: 'pointer',
              textDecoration: 'none',
              margin: '0 10px',
              transition: 'color 0.3s ease',
              fontSize: '10px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffd700'}
            onMouseLeave={(e) => e.target.style.color = '#bdb7b7'}
          >
            プライバシーポリシー
          </a>
        </div>
        <div style={{

          color: '#747272',
          fontSize: '12px'
        }}> 
          Copyright © 2025 スタート. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default UserPage; 